'use client';

import React from 'react';
import { Avatar, Button, Tooltip } from '@heroui/react';
import { LuBuilding, LuFileText, LuExternalLink, LuCheck } from 'react-icons/lu';
import { Notification } from '@/util/types/serverTypeCollection/activity';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { useTZDayjs } from '@/context/TimezoneContext';
import FullScreenLoader from '@/components/ui/FullScreenLoader';
import { parseContentWithMentions } from '@/util/functions/data/parseContentWithMentions';

type NotificationsListProps = {
  data: Notification[] | undefined;
  loading: boolean;
  mutate: () => void;
  onCountChange?: () => void;
  emptyMessage?: string;
};

type NotificationItemProps = {
  notification: Notification;
  isLast: boolean;
  onMarkAsRead: (id: string) => Promise<void>;
  onOpenSource: (notification: Notification) => void;
};

function NotificationItem({
  notification,
  isLast,
  onMarkAsRead,
  onOpenSource,
}: NotificationItemProps) {
  const tzDayjs = useTZDayjs();

  const getSourceInfo = () => {
    if (notification.inspection) {
      return {
        type: 'Inspection',
        icon: <LuBuilding size={10} />,
        label: notification.inspection.property?.street || `#${notification.inspection._id}`,
        colorClass: 'border-success-200 bg-success-50 text-success-700',
      };
    }
    if (notification.quote) {
      return {
        type: 'Quote',
        icon: <LuFileText size={10} />,
        label: notification.quote.property?.street || `#${notification.quote._id}`,
        colorClass: 'border-warning-200 bg-warning-50 text-warning-700',
      };
    }
    return null;
  };

  const source = getSourceInfo();

  return (
    <div
      className={`group relative flex items-center gap-3 p-3 transition-colors hover:bg-gray-100 ${
        !isLast ? 'border-b border-gray-100' : ''
      } ${notification.read ? 'bg-gray-50' : ''}`}
    >
      {/* Unread indicator */}
      <div
        className={`absolute bottom-1 left-1 top-1 w-1 rounded-full ${
          notification.read ? 'bg-gray-200' : 'bg-primary'
        }`}
      />

      {/* Avatar */}
      <div className='shrink-0 pl-1'>
        <Avatar
          src={notification.employee?.thumb || notification.employee?.image}
          name={notification.employee?.name || 'Someone'}
          size='sm'
          className='h-8 w-8 ring-2 ring-white'
        />
      </div>

      {/* Content */}
      <div className='min-w-0 flex-1 pr-20'>
        {/* Header: Name + title + source */}
        <div className='flex flex-wrap mb-1 items-center gap-x-1.5 gap-y-0.5'>
          <span
            className={`text-sm font-semibold ${notification.read ? 'text-gray-600' : 'text-gray-900'}`}
          >
            {notification.employee?.name || 'Someone'}
          </span>
          <span className='text-xs text-gray-500'>
            {notification.title} · {tzDayjs(notification.createdAt).fromNow()}
          </span>

          {/* <span className='text-xs text-gray-500'></span> */}
          {source && (
            <span
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0 text-[10px] font-semibold ${source.colorClass}`}
            >
              {source.icon}
              {source.type} - {source.label}
            </span>
          )}
        </div>

        {/* Message content with styled mentions */}
        <p className={`text-xs ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
          {parseContentWithMentions(notification.message)}
        </p>
      </div>

      {/* Hover actions */}
      <div className='absolute right-3 flex-wrap top-1/2 flex -translate-y-1/2 translate-x-2 items-center gap-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100'>
        <Button
          size='sm'
          variant='light'
          color='primary'
          onPress={() => onOpenSource(notification)}
          className='h-7 w-7 min-w-0'
          isIconOnly
        >
          <LuExternalLink size={14} />
        </Button>
        {!notification.read && !notification._targetGroup && (
          <Tooltip content='Mark as read' size='sm'>
            <Button
              size='sm'
              variant='light'
              color='success'
              isIconOnly
              onPress={() => onMarkAsRead(notification._id)}
              className='h-7 w-7 min-w-0'
            >
              <LuCheck size={16} />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default function NotificationsList({
  data,
  loading,
  mutate,
  onCountChange,
  emptyMessage = 'No notifications',
}: NotificationsListProps) {
  const handleMarkAsRead = async (id: string) => {
    try {
      await callServerAction({
        method: 'patch',
        url: `notification/${id}/read`,
        data: {},
      });
      mutate();
      onCountChange?.();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleOpenSource = (notification: Notification) => {
    // Mark as read (fire and forget)
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }

    // Open in new tab
    if (notification.inspection) {
      window.open(`/inspections/${notification.inspection._id}?tab=activity-feed`, '_blank');
    } else if (notification.quote) {
      window.open(`/work/quotes?quoteId=${notification.quote._id}`, '_blank');
    }
  };

  if (loading) {
    return <FullScreenLoader height={180} />;
  }

  if (!data || data.length === 0) {
    return (
      <div className='flex h-32 items-center justify-center text-sm text-gray-400'>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className='max-h-80 overflow-y-auto rounded-lg'>
      {data.map((notification, index) => (
        <NotificationItem
          key={notification._id}
          notification={notification}
          isLast={index === data.length - 1}
          onMarkAsRead={handleMarkAsRead}
          onOpenSource={handleOpenSource}
        />
      ))}
    </div>
  );
}

// Keep backwards-compatible alias
export { NotificationsList as MentionsList };
